import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  ViewEncapsulation,
} from '@angular/core';
import {
  DeclarativeMapCanvasComponent,
  DeclarativeMapMarkerComponent,
  DeclarativeMapMarkerTemplateDirective,
} from '@cue/map/elements';
import { NavigationGeodataBM } from '@cue/data';
import { cueQrCode, cueStart } from '@cue/ui/icons';
import { translation } from '@cue/translations';
import { LocalizedString, ShowOnFloorplanWhenActiveRouteEnum } from '@cue/api';
import { ImageDimensionsPipe, LocalizedStringPipe } from '@cue/utilities';
import { MAP_ACTIVE_Z_INDEX_OFFSET } from '@cue/map/core';

export type MapTargetPoint = {
  poi?: NavigationGeodataBM;
  name: LocalizedString;
  zoomPOI: boolean;
  poiSize: number;
  x: number;
  showOnFloorplanAbove: ShowOnFloorplanWhenActiveRouteEnum;
  y: number;
  icon: string | null;
  image: string | null;
};

@Component({
  selector: 'cue-map-target-point',
  imports: [
    DeclarativeMapMarkerComponent,
    DeclarativeMapMarkerTemplateDirective,
    LocalizedStringPipe,
    ImageDimensionsPipe,
  ],
  templateUrl: './map-target-point.component.html',
  styleUrl: './map-target-point.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MapTargetPointComponent {
  protected readonly activeZIndexOffset = MAP_ACTIVE_Z_INDEX_OFFSET;
  ICONS = {
    cueStart,
    cueQrCode,
  };

  canvas = inject(DeclarativeMapCanvasComponent);

  zoom = computed(() => {
    return this.canvas.zoom();
  });
  click = output();

  targetPoint = input<MapTargetPoint>();

  geoJson = computed(() => {
    if (!this.targetPoint()) return null;
    return {
      type: 'Point',
      coordinates: [this.targetPoint()?.x, this.targetPoint()?.y],
    };
  });

  focused = input<boolean, boolean | 'true' | 'false'>(false, {
    transform: booleanAttribute,
  });

  sizeCoefficient = computed(() => {
    return this.targetPoint()?.poiSize ?? 1;
  });

  scale = computed(() => {
    if (this.focused()) return 1.25;
    if (this.zoom() >= 0.5) {
      return this.sizeCoefficient() * this.zoom();
    } else {
      return this.sizeCoefficient() * 0.5;
    }
  });

  onHostClick(ev: MouseEvent) {
    ev.stopPropagation();
    this.click.emit();
  }

  protected readonly translation = translation;
  protected readonly showOnFloorplanAboveStartTargetEnum = ShowOnFloorplanWhenActiveRouteEnum;
}
